import React from "react";
import Price from "./price";
import { Link } from "react-router-dom";
import { PlayerStore, Store } from "../store";
import useWantlist from "../hooks/useWantlist";
import { useDefaultImage } from "../hooks/useInventory";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { PlayerButtonDetailed } from "../routes/item";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

const Tile = ({ item }) => {
  const config = Store.useState(s => s.config);
  const forSaleListings = item.listings ? item.listings.filter(l => l.stock.quantity > 0) : [];
  const cheapest = forSaleListings.length ? forSaleListings[0] : null;
  const defaultImage = useDefaultImage();
  const wantlistEnabled = config.eshop.theme.selected.settings?.wantlist?.enabled;

  if (item.type === "ReleaseItem")
    return <ReleaseTile item={item} config={config} defaultImage={defaultImage} cheapest={cheapest} listings={forSaleListings} wantlistEnabled={wantlistEnabled} />;

  else if (item.type === "ProductItem")
    return <ProductTile item={item} defaultImage={defaultImage} cheapest={cheapest} listings={forSaleListings} wantlistEnabled={wantlistEnabled} />;

  else if (item.type === "BookItem")
    return <BookTile item={item} defaultImage={defaultImage} cheapest={cheapest} listings={forSaleListings} wantlistEnabled={wantlistEnabled} />;
};

const ReleaseTile = ({ item, config, cheapest, defaultImage, wantlistEnabled }) => {
  const { addToWantlist } = useWantlist();
  const path = item.path;
  const { loadTracklist, currentTrackData, isPlaying, playPauseTrack } = PlayerStore.useState(s => s);

  const buttonIsPlaying = isPlaying && currentTrackData && currentTrackData.release._id === item._id;
  const active = config.eshop.theme.selected.player?.active;

  const tracklistToLoad = {
    tracklist: item.data.tracklist.filter(t => t.uri).map(t => ({ track: t, release: item })) || [],
    playOffset: 0
  };

  return (<div className='tile releaseItem'>
    <Link to={path} className="artwork">
      <LazyLoadImage src={item.data?.images[0]?.uri || defaultImage} />
    </Link>
    <div className="priceAndActions">
      <PriceAndSecondHandSection listing={cheapest} />
      <div className="actions">
        {active && item.data.tracklist.filter(t => t.uri).length ? <button type="button" onClick={e => buttonIsPlaying ? playPauseTrack() : loadTracklist(tracklistToLoad)}>
          <i className={`cg-icon-${buttonIsPlaying ? "pause" : "play"}`} />
        </button> : null}
        {wantlistEnabled ? <button type="button" onClick={e => addToWantlist(item)}>
          <i className={item.wanted ? "cg-icon-wantlist-fill" : "cg-icon-wantlist"}/>
        </button> : null }
      </div>
    </div>
    <ArtistNames artists={item.data.artists} />
    <Link to={path}>{item.data.title}</Link>
  </div>);
};

export function ArtistNames (props) {
  return (
    <p className='artists'>
      {props.artists.map((a, index) => (
        <span key={index + a.name}>
          {a.id ? <Link to={`/catalogue?artists=${a.id}`}>{a.anv || a.name} </Link> : `${a.anv || a.name}`}
        </span>
      ))}
    </p>
  );
}

const PriceAndSecondHandSection = ({ listing }) => {
  return <p className="price">
    {listing ? <Price listing={listing} /> : "N/A"}
    {listing?.prices.compare ? <Price className="discounted" value={listing.prices.compare} /> : null}
    {listing?.secondHand ? <span className="secondHand"></span> : null}
  </p>;
};

const ProductTile = ({ item, cheapest, defaultImage, wantlistEnabled }) => {
  const { addToWantlist } = useWantlist();

  const path = item.path;

  return (<div className='tile productItem'>
    <Link to={path}>
      <LazyLoadImage src={item.data?.images[0]?.uri || defaultImage} />
    </Link>
    <div className="priceAndActions">
      <PriceAndSecondHandSection listing={cheapest} />
      <div className="actions">
        { wantlistEnabled ? <button type="button" onClick={e => addToWantlist(item)}>
          <i className={item.wanted ? "cg-icon-wantlist-fill" : "cg-icon-wantlist"}/>
        </button> : null }
      </div>
    </div>
    <Link to={path}>{item.data.title}</Link>
  </div>);
};

const BookTile = ({ item, cheapest, defaultImage, wantlistEnabled }) => {
  const { addToWantlist } = useWantlist();

  const path = item.path;

  return (<div className='tile productItem'>
    <Link to={path}>
      <LazyLoadImage src={item.data?.images[0]?.uri || defaultImage} />
    </Link>
    <div className="priceAndActions">
      <PriceAndSecondHandSection listing={cheapest} />
      <div className="actions">
        {wantlistEnabled ? <button type="button" onClick={e => addToWantlist(item)}>
          <i className={item.wanted ? "cg-icon-wantlist-fill" : "cg-icon-wantlist"}/>
        </button> : null}
      </div>
    </div>
    <Link to={path}>{item.data.title} {item.data.subtitle ? <span>-{item.data.subtitle}</span> : null}</Link>
  </div>);
};

const List = ({ item }) => {
  const config = Store.useState(s => s.config);
  const forSaleListings = item.listings ? item.listings.filter(l => l.stock.quantity > 0) : [];
  const cheapest = forSaleListings.length ? forSaleListings[0] : null;
  const defaultImage = useDefaultImage();
  const wantlistEnabled = config.eshop.theme.selected.settings?.wantlist?.enabled;

  const path = item.path;
  const ReleaseList = ({ item }) => {
    const Tracklist = ({ item }) => {
      const isRelease = item.type === "ReleaseItem";

      return <>
      {isRelease ? (
        <div className="tracklist">
          {item.data.tracklist.filter((e, i) => i < 6)
            .filter(t => t.type_ === "track")
            .map((t, index) =>
              <PlayerButtonDetailed
                key={`${t.position}-${index}`}
                track={t}
                release={item}
              />
            )}
        </div>
      ) : null} </>;
    };

    return (<div className='list releaseItem'>
      <Link to={path} className="artwork">
        <LazyLoadImage src={item.data?.images[0]?.uri || defaultImage} />
      </Link>
      <div className="right">
        <ArtistNames artists={item.data.artists} />
        <p className="title"><Link to={path}>{item.data.title}</Link></p>
        <PriceAndSecondHandSection listing={cheapest} />
        <Specs item={item} />
        <p className="description" dangerouslySetInnerHTML={{ __html: item.descriptions.shop.short }} />
        <Tracklist item={item} />

      </div>
    </div>);
  };

  const ProductList = ({ item }) => {
    return (<div className='list releaseItem'>
      <Link to={path} className="artwork">
        <LazyLoadImage src={item.data?.images[0]?.uri || defaultImage} />
      </Link>
      <div className="right">
        <p className="title"><Link to={path}>{item.data.title}</Link></p>
        <PriceAndSecondHandSection listing={cheapest} />
        <Specs item={item} />
      </div>
    </div>);
  };

  const BookList = ({ item }) => {
    return (<div className='list releaseItem'>
      <Link to={path} className="artwork">
        <LazyLoadImage src={item.data?.images[0]?.uri || defaultImage} />
      </Link>
      <div className="right">
        <p className="title"><Link to={path}>{item.data.title}</Link></p>
        <PriceAndSecondHandSection listing={cheapest} />
        <Specs item={item} />
      </div>
    </div>);
  };

  if (item.type === "ReleaseItem")
    return <ReleaseList item={item} config={config} defaultImage={defaultImage} cheapest={cheapest} listings={forSaleListings} wantlistEnabled={wantlistEnabled} />;

  else if (item.type === "ProductItem")
    return <ProductList item={item} defaultImage={defaultImage} cheapest={cheapest} listings={forSaleListings} wantlistEnabled={wantlistEnabled} />;

  else if (item.type === "BookItem")
    return <BookList item={item} defaultImage={defaultImage} cheapest={cheapest} listings={forSaleListings} wantlistEnabled={wantlistEnabled} />;
};

const Specs = ({ item }) => {
  const { t } = useTranslation();

  const LabelNames = () => {
    return item.data.labels.map((l, index) => (
      <span key={index + l.name}>{l.id ? <Link to={`/catalogue?labels=${l.id}`}>{l.name} </Link> : `${l.name}`} ({l.catno}) <br /></span>
    ));
  };

  if (item.type === "ReleaseItem")
    return <div className="specs">
      <p className="labels">
        <LabelNames />
      </p>
      {item.data.releaseDate ? <p>
        {item.data.country ? <Link to={`/catalogue?countries=${item.data.country}`}>{item.data.country}</Link> : null}
        </p> : null}
      <div className="styles">
        {item.data.styles.map(s => <Link key={s} to={`/catalogue?styles=${s}`}>
          {s}{" "}
        </Link>)}
      </div>
    </div>;

  else if (item.type === "ProductItem")
    return <div className="specs">
      <p>{item.data.manufacturer} {item.data.cat ? `(${item.data.cat})` : ""}</p>
    </div>;
  else if (item.type === "BookItem")
    return <div className="specs">
      <p>{t("publisher")}: {item.data.publisher}</p>
      <p>{t("pages")}: {item.data.pageCount}</p>
      <p>{t("categories")}: {item.data.categories.join(", ")}</p>
      <p>{t("language")}: {item.data.language}</p>
      {item.data.publishedDate ? <p>{t("publicationDate")}: {DateTime.fromMillis(item.data.publishedDate).toFormat("DD")}</p> : null}
      {Object.keys(item.data.industryIdentifiers).map(key =>
        <p key={key}>{item.data.industryIdentifiers[key].type}: <span>{item.data.industryIdentifiers[key].identifier}</span></p>)
      }
    </div>;
  else return null;
};

export { Tile, List };
